/**
 * Guardian subscriptions — personal Stripe billing on the shared BillingAccount.
 *
 * Ended subscriptions are not shown. A live sub (active / trialing / past_due /
 * cancel-at-period-end) is current until currentPeriodEnd. Choose is not wired
 * to Checkout while a personal sub is live (plan-change API is separate).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  cancelGuardianSubscription,
  confirmGuardianCheckoutSession,
  createGuardianCheckoutSession,
  createGuardianPortalSession,
  fetchGuardianInvoices,
  fetchGuardianPlans,
  fetchGuardianSubscription,
  resumeGuardianSubscription,
} from '../api/guardianApi';
import {
  GUARDIAN_PLANS,
  annualCentsFromMonthly,
  annualTrialCentsFromMonthly,
  displayPeriodCents,
  formatUsdFromCents,
  mapBillingPlansToGuardianPlans,
  planIdFromEntitlementTier,
} from '../data/guardianPlans';
import {
  SELF_SERVE_PLAN_IDS,
  canChoosePaidPlan,
  buildCheckoutRequest,
} from '../data/guardianCheckout';

const brand = {
  navy: '#0c1e4a',
  blue: '#2b7fff',
  blueDark: '#1a5fd4',
  blueLight: '#e8f2ff',
  ink: '#0f172a',
  muted: '#64748b',
  rule: '#e2e8f0',
};

const SALES_MAILTO = 'mailto:sales@interlynked.com?subject=Enterprise%20pricing';

function formatPeriodDate(iso) {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatInvoiceDate(iso) {
  return formatPeriodDate(iso) || '—';
}

function BillingToggle({ billing, onChange }) {
  return (
    <Box
      role="group"
      aria-label="Billing period"
      sx={{
        display: 'inline-flex',
        border: `1px solid ${brand.rule}`,
        borderRadius: 1.5,
        p: 0.5,
        bgcolor: '#fff',
      }}
    >
      {(['monthly', 'annual']).map((option) => {
        const active = billing === option;
        return (
          <Button
            key={option}
            size="small"
            onClick={() => onChange(option)}
            aria-pressed={active}
            sx={{
              textTransform: 'capitalize',
              minWidth: 88,
              px: 2,
              py: 0.75,
              borderRadius: 1,
              fontWeight: 600,
              color: active ? '#fff' : brand.muted,
              bgcolor: active ? brand.blue : 'transparent',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: active ? brand.blueDark : brand.blueLight,
                boxShadow: 'none',
              },
            }}
          >
            {option}
          </Button>
        );
      })}
    </Box>
  );
}

function PlanCard({
  plan,
  billing,
  isCurrent,
  cancelsOn,
  checkoutBusy,
  allowChoose,
  onChoose,
}) {
  const amount = displayPeriodCents(
    plan.priceMonthlyCents,
    plan.priceAnnualCents,
    billing,
  );
  const periodLabel = billing === 'annual' ? 'year' : 'month';
  const isTeamContact = plan.ctaKind === 'contact' || plan.audience === 'team';
  const showChoose = allowChoose && SELF_SERVE_PLAN_IDS.has(plan.id);
  const showFreemiumCta = plan.id === 'freemium' && isCurrent;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        borderRadius: 2,
        borderColor: isCurrent || plan.popular ? brand.blue : brand.rule,
        boxShadow: isCurrent || plan.popular ? `0 0 0 1px ${brand.blue}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.ink }}>
            {plan.name}
          </Typography>
          {isCurrent ? (
            <Chip
              label={cancelsOn ? `Cancels ${cancelsOn}` : 'Current plan'}
              size="small"
              sx={{
                bgcolor: cancelsOn ? '#fff7ed' : brand.blue,
                color: cancelsOn ? '#9a3412' : '#fff',
                border: cancelsOn ? '1px solid #fdba74' : 'none',
                fontWeight: 600,
                height: 22,
                fontSize: 11,
              }}
            />
          ) : plan.popular ? (
            <Chip
              label="Most popular"
              size="small"
              sx={{
                bgcolor: brand.blueLight,
                color: brand.navy,
                fontWeight: 600,
                height: 22,
                fontSize: 11,
              }}
            />
          ) : null}
        </Box>
        <Typography variant="body2" sx={{ color: brand.muted, mt: 0.75, minHeight: 40 }}>
          {plan.description}
        </Typography>
      </Box>

      <Box sx={{ px: 2.5, pb: 2 }}>
        {amount === 0 ? (
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: brand.ink }}>
            Free
          </Typography>
        ) : amount == null ? (
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: brand.ink }}>
            Custom pricing
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: brand.ink }}>
            {formatUsdFromCents(amount)}
            <Typography
              component="span"
              variant="body2"
              sx={{ color: brand.muted, fontWeight: 400, ml: 0.5 }}
            >
              / {periodLabel}
            </Typography>
          </Typography>
        )}
        {plan.priceNote && (
          <Typography variant="caption" sx={{ color: brand.muted, display: 'block', mt: 0.5 }}>
            {plan.priceNote}
          </Typography>
        )}
        {SELF_SERVE_PLAN_IDS.has(plan.id) && plan.priceMonthlyCents > 0 && billing === 'annual' ? (
          <Typography variant="caption" sx={{ color: brand.muted, display: 'block', mt: 0.5 }}>
            1-week trial at checkout, then{' '}
            {formatUsdFromCents(annualTrialCentsFromMonthly(plan.priceMonthlyCents))}/year
          </Typography>
        ) : null}
      </Box>

      <Box component="ul" sx={{ listStyle: 'none', m: 0, px: 2.5, pb: 2, flex: 1 }}>
        {plan.features.map((feature) => (
          <Box
            component="li"
            key={feature}
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              mb: 1.25,
            }}
          >
            <CheckIcon
              sx={{ color: brand.blue, fontSize: 18, mt: 0.15, flexShrink: 0 }}
              aria-hidden
            />
            <Typography variant="body2" sx={{ color: brand.ink, lineHeight: 1.4 }}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 2.5, pb: 2.5, mt: 'auto' }}>
        {isTeamContact ? (
          <Button
            fullWidth
            variant="contained"
            href={SALES_MAILTO}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              py: 1.1,
              bgcolor: brand.blue,
              '&:hover': { bgcolor: brand.blueDark },
            }}
          >
            {plan.ctaLabel}
          </Button>
        ) : isCurrent || showFreemiumCta ? (
          <Button
            fullWidth
            variant="outlined"
            disabled
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              py: 1.1,
              borderColor: brand.rule,
              color: brand.muted,
            }}
          >
            Current plan
          </Button>
        ) : showChoose ? (
          <Button
            fullWidth
            variant="contained"
            disabled={checkoutBusy}
            onClick={() => onChoose(plan)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              py: 1.1,
              bgcolor: brand.blue,
              '&:hover': { bgcolor: brand.blueDark },
            }}
          >
            {plan.ctaLabel}
          </Button>
        ) : null}
      </Box>
    </Paper>
  );
}

/**
 * @param {object} props
 * @param {() => void} props.onBack
 * @param {string|null|undefined} [props.currentTier]
 * @param {string|null|undefined} [props.authToken]
 * @param {(user: object, entitlements: object) => void} [props.onProfileLoaded]
 */
export default function GuardianPricingPage({
  onBack,
  currentTier,
  authToken,
  onProfileLoaded,
}) {
  const [billing, setBilling] = useState('monthly');
  const [plans, setPlans] = useState(GUARDIAN_PLANS);
  const [loading, setLoading] = useState(Boolean(authToken));
  const [error, setError] = useState(null);
  const [fromApi, setFromApi] = useState(false);
  const [checkoutBusyId, setCheckoutBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showInvoices, setShowInvoices] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [trialPick, setTrialPick] = useState(null);

  const liveSub = subscription;
  const cancelsOn = liveSub?.cancelAtPeriodEnd
    ? formatPeriodDate(liveSub.currentPeriodEnd)
    : '';

  useEffect(() => {
    if (!authToken) {
      setPlans(GUARDIAN_PLANS);
      setLoading(false);
      setFromApi(false);
      setSubscription(null);
      setInvoices([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const [plansRes, subRes, invoiceRes] = await Promise.all([
        fetchGuardianPlans(authToken),
        fetchGuardianSubscription(authToken),
        fetchGuardianInvoices(authToken),
      ]);
      if (cancelled) return;
      if (!plansRes.success) {
        setError(plansRes.message || 'Could not load plans from the server. Showing local catalog.');
        setPlans(GUARDIAN_PLANS);
        setFromApi(false);
      } else {
        setPlans(mapBillingPlansToGuardianPlans(plansRes.data));
        setFromApi(true);
      }
      if (subRes.success) {
        setSubscription(subRes.data || null);
      }
      if (invoiceRes.success && Array.isArray(invoiceRes.data)) {
        setInvoices(invoiceRes.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (checkout === 'canceled' || checkout === 'cancel') {
      setNotice({
        severity: 'info',
        message: 'Checkout was canceled. Your current plan is unchanged.',
      });
    }
    if (checkout || sessionId) {
      params.delete('checkout');
      params.delete('session_id');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', next);
    }
    if (!authToken) return undefined;
    const shouldConfirm = checkout === 'success' || Boolean(sessionId);
    if (!shouldConfirm) return undefined;
    let cancelled = false;
    (async () => {
      const res = await confirmGuardianCheckoutSession(authToken, { sessionId });
      if (cancelled || !res.success) return;
      if (res.data?.subscription) {
        setSubscription(res.data.subscription);
      }
      if (res.data?.synced) {
        setNotice({
          severity: 'success',
          message:
            'Payment received. This plan is shared with the Interlinked platform — one subscription covers both.',
        });
      }
      if (onProfileLoaded) {
        onProfileLoaded(res.data?.user, res.data?.entitlements);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, onProfileLoaded]);

  const handleChoose = (plan) => {
    if (!canChoosePaidPlan(plan.id, liveSub)) return;
    if (!authToken) {
      setError('Sign in to start checkout. The same subscription applies on both platforms.');
      return;
    }
    setError(null);
    setTrialPick(plan);
  };

  const startCheckout = async (plan, startTrial) => {
    if (!plan || !authToken) return;
    setCheckoutBusyId(plan.id);
    const res = await createGuardianCheckoutSession(
      authToken,
      buildCheckoutRequest({
        planSlug: plan.id,
        period: billing,
        startTrial,
      }),
    );
    setCheckoutBusyId(null);
    if (!res.success || !res.data?.url) {
      setError(res.message || 'Could not start checkout. Try again or contact support.');
      return;
    }
    window.location.assign(res.data.url);
  };

  const handleConfirmCancel = async () => {
    if (!authToken) return;
    setActionBusy('cancel');
    const res = await cancelGuardianSubscription(authToken);
    setActionBusy(null);
    if (!res.success) {
      setError(res.message || 'Could not cancel the subscription.');
      return;
    }
    setSubscription(res.data || null);
    setCancelOpen(false);
    setNotice({
      severity: 'info',
      message: res.data?.currentPeriodEnd
        ? `Auto-renew is off. You keep access until ${formatPeriodDate(res.data.currentPeriodEnd)}.`
        : 'Auto-renew is off. You keep access until the period ends.',
    });
  };

  const handleResume = async () => {
    if (!authToken) return;
    setActionBusy('resume');
    const res = await resumeGuardianSubscription(authToken);
    setActionBusy(null);
    if (!res.success) {
      setError(res.message || 'Could not resume auto-renew.');
      return;
    }
    setSubscription(res.data || null);
    setNotice({
      severity: 'success',
      message: 'Auto-renew is on again. Your plan will continue after this period.',
    });
  };

  const handlePaymentMethod = async () => {
    if (!authToken) return;
    setActionBusy('portal');
    const res = await createGuardianPortalSession(
      authToken,
      `${window.location.origin}/subscriptions`,
    );
    setActionBusy(null);
    if (!res.success || !res.data?.url) {
      setError(res.message || 'Could not open the payment-method portal.');
      return;
    }
    window.location.assign(res.data.url);
  };

  const currentPlanId = liveSub?.planSlug
    ? planIdFromEntitlementTier(liveSub.planSlug)
    : 'freemium';
  const personalPlans = useMemo(
    () => plans.filter((p) => p.audience === 'personal'),
    [plans],
  );
  const teamPlans = useMemo(
    () => plans.filter((p) => p.audience === 'team'),
    [plans],
  );
  const currentPlan =
    plans.find((p) => p.id === currentPlanId) || personalPlans[0] || plans[0];

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2, pb: 6 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, textTransform: 'none', color: brand.navy }}
      >
        Back to chat
      </Button>

      {liveSub ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            borderColor: brand.rule,
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'flex-start' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: brand.ink }}>
                Current subscription
              </Typography>
              <Typography variant="body2" sx={{ color: brand.muted, mt: 0.5 }}>
                One Stripe subscription covers Guardian and the Interlinked platform.
                Auto-renew is on unless you cancel.
              </Typography>
            </Box>
            <Chip
              label={
                liveSub.cancelAtPeriodEnd && cancelsOn
                  ? `Cancels ${cancelsOn}`
                  : liveSub.planSlug || currentPlanId
              }
              size="small"
              sx={{
                textTransform: liveSub.cancelAtPeriodEnd ? 'none' : 'capitalize',
                bgcolor: liveSub.cancelAtPeriodEnd ? '#fff7ed' : brand.blueLight,
                color: liveSub.cancelAtPeriodEnd ? '#9a3412' : brand.navy,
                fontWeight: 600,
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: brand.ink }}>
            {liveSub.planName || currentPlan?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: brand.muted, mt: 0.5, mb: 2 }}>
            {liveSub.cancelAtPeriodEnd
              ? `You are on ${liveSub.planName || currentPlan?.name} until ${cancelsOn || 'the period ends'}. Resume auto-renew to keep the plan.`
              : `You are on the ${liveSub.planName || currentPlan?.name} plan.`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {liveSub.cancelAtPeriodEnd ? (
              <Button
                variant="contained"
                disabled={actionBusy === 'resume'}
                onClick={handleResume}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: brand.blue,
                  '&:hover': { bgcolor: brand.blueDark },
                }}
              >
                {actionBusy === 'resume' ? 'Resuming…' : 'Resume auto-renew'}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setCancelOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel subscription
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setShowInvoices((open) => !open)}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: brand.blue, color: brand.blue }}
            >
              {showInvoices ? 'Hide invoices' : 'Invoices'}
            </Button>
            <Button
              variant="outlined"
              disabled={actionBusy === 'portal'}
              onClick={handlePaymentMethod}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: brand.blue, color: brand.blue }}
            >
              {actionBusy === 'portal' ? 'Opening…' : 'Payment method'}
            </Button>
          </Box>
          {showInvoices && (
            <Box sx={{ mt: 2, borderTop: `1px solid ${brand.rule}`, pt: 2 }}>
              {invoices.length === 0 ? (
                <Typography variant="body2" sx={{ color: brand.muted }}>
                  No invoices yet.
                </Typography>
              ) : (
                invoices.map((invoice) => (
                  <Box
                    key={invoice.id || invoice.stripeInvoiceId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1,
                      borderBottom: `1px solid ${brand.rule}`,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: brand.ink }}>
                        {invoice.description || 'Subscription invoice'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: brand.muted }}>
                        {formatInvoiceDate(invoice.createdAt)} · {invoice.status}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2">
                        {typeof invoice.amount === 'number'
                          ? `$${Number(invoice.amount).toFixed(2)}`
                          : invoice.amount}
                      </Typography>
                      {invoice.pdfUrl ? (
                        <Button
                          size="small"
                          href={invoice.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ textTransform: 'none', minWidth: 0 }}
                        >
                          PDF
                        </Button>
                      ) : null}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Paper>
      ) : null}

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.ink }}>
            Plans
          </Typography>
          <Typography variant="body2" sx={{ color: brand.muted }}>
            {liveSub
              ? 'A personal plan is already active. Upgrade and downgrade will use the plan-change API — Choose is not offered while this subscription is live.'
              : 'Choose a paid personal plan to add a payment method. Freemium has no trial. On annual, a 1-week trial is 11.75× monthly; skip the trial and pay 11.5×.'}
            {fromApi ? ' Prices loaded from your account catalog.' : ''}
          </Typography>
        </Box>
        <BillingToggle billing={billing} onChange={setBilling} />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
              mb: 5,
            }}
          >
            {personalPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billing={billing}
                isCurrent={currentPlanId === plan.id}
                cancelsOn={currentPlanId === plan.id ? cancelsOn : ''}
                checkoutBusy={checkoutBusyId === plan.id}
                allowChoose={canChoosePaidPlan(plan.id, liveSub)}
                onChoose={handleChoose}
              />
            ))}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: brand.ink }}>
              Teams & Enterprise
            </Typography>
            <Typography variant="body2" sx={{ color: brand.muted }}>
              Built for agencies and organizations that need shared access at scale.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 4,
            }}
          >
            {teamPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billing={billing}
                isCurrent={false}
                cancelsOn=""
                checkoutBusy={false}
                allowChoose={false}
                onChoose={handleChoose}
              />
            ))}
          </Box>
        </>
      )}

      <Typography
        variant="body2"
        sx={{
          color: brand.muted,
          lineHeight: 1.6,
          width: '100%',
        }}
      >
        Every plan includes access to the Interlinked platform. Higher tiers unlock deeper
        intelligence, collaboration, and integrations — so your team can use the full set of
        capabilities that match how you work.
      </Typography>

      <Dialog
        open={Boolean(trialPick)}
        onClose={() => setTrialPick(null)}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Start {trialPick?.name || 'this plan'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: brand.muted }}>
            Add a payment method on Stripe Checkout. Freemium stays free with no trial.
            {billing === 'annual'
              ? ' A 1-week trial bills 11.75× monthly after the week; skip the trial and pay 11.5× now.'
              : ' You can start a 1-week trial at the monthly price, or subscribe now.'}
          </Typography>
          {trialPick?.priceMonthlyCents > 0 && billing === 'annual' ? (
            <Typography variant="body2" sx={{ color: brand.ink, mt: 1.5, fontWeight: 600 }}>
              Trial: {formatUsdFromCents(annualTrialCentsFromMonthly(trialPick.priceMonthlyCents))}/year after 1 week
              <br />
              No trial: {formatUsdFromCents(trialPick.priceAnnualCents || annualCentsFromMonthly(trialPick.priceMonthlyCents))}/year
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setTrialPick(null)} sx={{ textTransform: 'none' }}>
            Go back
          </Button>
          <Button
            variant="outlined"
            disabled={Boolean(checkoutBusyId)}
            onClick={() => startCheckout(trialPick, false)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {checkoutBusyId ? 'Starting…' : 'Subscribe now'}
          </Button>
          <Button
            variant="contained"
            disabled={Boolean(checkoutBusyId)}
            onClick={() => startCheckout(trialPick, true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: brand.blue,
              '&:hover': { bgcolor: brand.blueDark },
            }}
          >
            {checkoutBusyId ? 'Starting…' : 'Start 1-week trial'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel subscription?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: brand.muted }}>
            Auto-renew will stop. You keep {liveSub?.planName || 'this plan'} until{' '}
            {formatPeriodDate(liveSub?.currentPeriodEnd) || 'the end of the current period'}.
            This does not refund the current period.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none' }}>
            Go back
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={actionBusy === 'cancel'}
            onClick={handleConfirmCancel}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {actionBusy === 'cancel' ? 'Canceling…' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
